import { z } from 'zod';
import { arrayQueryParam, nonEmptyTrimmed, pageSchema, paginationQuerySchema, sortDirectionSchema, uuidSchema } from '../common/primitives';
import {
  supportPlanAlertTypeSchema,
  supportPlanCurrencySchema,
  supportPlanDetailSchema,
  supportPlanScopeSchema,
  supportPlanTypeSchema,
} from './support-plan.contract';

/**
 * Una fila de `support_invoices` es un hecho consumado, no un estado: que
 * exista alguna para un `(planId, periodKey)` YA significa "facturado". El
 * total de un período se puede dividir en varias facturas (varias filas del
 * mismo período), que se registran todas juntas. Su suma es el monto
 * congelado del período — puede diferir del calculado por Kora (p. ej. una
 * cuota fija que no cobra el excedente); la UI avisa la diferencia. El estado de pago es de cada factura y se deriva al leer
 * (`paidAt` → pagado; `dueAt` vencido → atrasado; si no, pendiente) — nunca
 * se guarda, ver el comentario en `packages/db/src/schema/tickets.ts`. Kora
 * no emite la factura: solo registra que ya se emitió y cuándo se pagó.
 */

/** Tope de facturas en que se puede dividir el total de un período. */
export const MAX_SUPPORT_INVOICES_PER_PERIOD = 10;

export const SUPPORT_INVOICE_BILLING_STATUSES = ['to_invoice', 'invoiced'] as const;
export const supportInvoiceBillingStatusSchema = z.enum(SUPPORT_INVOICE_BILLING_STATUSES);
export type SupportInvoiceBillingStatus = z.infer<typeof supportInvoiceBillingStatusSchema>;

export const SUPPORT_INVOICE_BILLING_STATUS_LABELS: Readonly<Record<SupportInvoiceBillingStatus, string>> = {
  to_invoice: 'Por facturar',
  invoiced: 'Facturado',
};

export const SUPPORT_INVOICE_PAYMENT_STATUSES = ['pending', 'overdue', 'paid'] as const;
export const supportInvoicePaymentStatusSchema = z.enum(SUPPORT_INVOICE_PAYMENT_STATUSES);
export type SupportInvoicePaymentStatus = z.infer<typeof supportInvoicePaymentStatusSchema>;

export const SUPPORT_INVOICE_PAYMENT_STATUS_LABELS: Readonly<Record<SupportInvoicePaymentStatus, string>> = {
  pending: 'Pago pendiente',
  overdue: 'Atrasado',
  paid: 'Pagado',
};

export const supportInvoiceSchema = z.object({
  id: uuidSchema,
  planId: uuidSchema,
  /** 'YYYY-MM'. Para `monthly` coincide con un `support_monthly_periods`; para `bundle`/`on_demand` es el mes calendario en que se emitió. */
  periodKey: z.string(),
  /** Congelado al facturar — no se recalcula después aunque cambien las horas registradas. */
  amountUf: z.number(),
  /** Moneda congelada junto con el monto — la del contrato al momento de facturar. */
  currency: supportPlanCurrencySchema,
  /** Horas del período facturado — cuando el total se dividió en varias facturas, solo las trae la primera (`position` 0); el resto, `null`. */
  hoursBilled: z.number().nullable(),
  /** Orden dentro de las facturas del período (0 = la primera). */
  position: z.number().int(),
  folio: z.string(),
  invoicedAt: z.iso.date(),
  dueAt: z.iso.date().nullable(),
  paidAt: z.iso.date().nullable(),
  attachmentName: z.string().nullable(),
  /** Derivado al leer — ver el comentario del schema de la tabla. */
  paymentStatus: supportInvoicePaymentStatusSchema,
});
export type SupportInvoice = z.infer<typeof supportInvoiceSchema>;

/** Una de las facturas en que se divide el total del período. */
export const supportInvoiceDocumentInputSchema = z.object({
  folio: nonEmptyTrimmed(50, 'El folio'),
  invoicedAt: z.iso.date(),
  /** Monto de esta factura. La suma de todas queda como el monto facturado del período, aunque difiera del calculado. */
  amountUf: z.number().positive('El monto de cada factura debe ser mayor a 0.'),
  attachmentName: z.string().trim().max(255).optional(),
});
export type SupportInvoiceDocumentInput = z.infer<typeof supportInvoiceDocumentInputSchema>;

export const markSupportInvoiceIssuedSchema = z.object({
  invoices: z
    .array(supportInvoiceDocumentInputSchema)
    .min(1, 'Ingresa al menos una factura.')
    .max(MAX_SUPPORT_INVOICES_PER_PERIOD, `El total se puede dividir en ${MAX_SUPPORT_INVOICES_PER_PERIOD} facturas como máximo.`)
    .refine((invoices) => new Set(invoices.map((invoice) => invoice.folio.trim())).size === invoices.length, 'Hay folios repetidos.'),
  /** Solo `monthly`: el período a facturar (el vigente o uno ya cerrado que quedó sin factura). Sin él, se factura el vigente. */
  monthlyPeriodId: uuidSchema.optional(),
});
export type MarkSupportInvoiceIssuedInput = z.infer<typeof markSupportInvoiceIssuedSchema>;

/** Una factura del período, resumida — para mostrar sus folios y marcarlas como pagadas una a una. */
export const supportInvoiceRefSchema = z.object({
  id: uuidSchema,
  folio: z.string(),
  amountUf: z.number(),
  paymentStatus: supportInvoicePaymentStatusSchema,
});
export type SupportInvoiceRef = z.infer<typeof supportInvoiceRefSchema>;

export const markSupportInvoicePaidSchema = z.object({
  paidAt: z.iso.date(),
});
export type MarkSupportInvoicePaidInput = z.infer<typeof markSupportInvoicePaidSchema>;

/**
 * `invoiced`: ya tiene factura. `to_invoice`: el período vigente o uno ya
 * cerrado sin factura — se puede registrar ahora. `upcoming`: un mes futuro
 * del contrato; se habilita cuando empieza (`startDate`).
 */
export const SUPPORT_MONTHLY_BILLING_PERIOD_STATUSES = ['invoiced', 'to_invoice', 'upcoming'] as const;
export const supportMonthlyBillingPeriodStatusSchema = z.enum(SUPPORT_MONTHLY_BILLING_PERIOD_STATUSES);
export type SupportMonthlyBillingPeriodStatus = z.infer<typeof supportMonthlyBillingPeriodStatusSchema>;

/** Un mes del calendario de facturación de un contrato `monthly`. */
export const supportMonthlyBillingPeriodSchema = z.object({
  /** `null` en los meses futuros: el período se crea recién cuando empieza. */
  monthlyPeriodId: uuidSchema.nullable(),
  /** 'YYYY-MM' — el mismo `periodKey` con que queda la factura. */
  periodKey: z.string(),
  startDate: z.iso.date(),
  /** Exclusivo: el período es [startDate, endDate). */
  endDate: z.iso.date(),
  isCurrent: z.boolean(),
  status: supportMonthlyBillingPeriodStatusSchema,
  /** Facturado: el monto congelado (la suma de sus facturas). Si no: la cuota (HH del período × valor hora, más el excedente si corresponde) — en los meses futuros es una estimación. */
  amountUf: z.number(),
  /** Las facturas del mes, en orden — vacío si todavía no se factura. */
  invoices: z.array(supportInvoiceRefSchema),
});
export type SupportMonthlyBillingPeriod = z.infer<typeof supportMonthlyBillingPeriodSchema>;

/** Ficha de contrato + su historial de facturas — la respuesta de `GET /support-plans/:id`. */
export const supportPlanWithInvoicesSchema = supportPlanDetailSchema.extend({
  invoices: z.array(supportInvoiceSchema),
  /** Solo `monthly` (vacío en bolsa/a demanda): todos los meses del contrato, del primero al último. */
  billingPeriods: z.array(supportMonthlyBillingPeriodSchema),
});
export type SupportPlanWithInvoices = z.infer<typeof supportPlanWithInvoicesSchema>;

/** Fila del panel de Facturación de Soporte — un contrato en el período seleccionado. */
export const supportBillingRowSchema = z.object({
  planId: uuidSchema,
  planName: z.string(),
  clientId: uuidSchema,
  clientName: z.string(),
  scope: supportPlanScopeSchema,
  planType: supportPlanTypeSchema,
  /** `null` = sin movimiento en este período (ni factura registrada ni monto en vivo — solo posible en períodos pasados). */
  amountUf: z.number().nullable(),
  currency: supportPlanCurrencySchema,
  billingStatus: supportInvoiceBillingStatusSchema.nullable(),
  /** Con varias facturas en el período: atrasado si alguna lo está; pagado solo si están todas pagadas. */
  paymentStatus: supportInvoicePaymentStatusSchema.nullable(),
  /** Facturas del período de la fila, en orden — para "Marcar como pagado" desde el panel. */
  invoices: z.array(supportInvoiceRefSchema),
  /** Solo en el período en curso, por facturar y con monto > 0 — mismas condiciones que exige `MarkSupportInvoiceIssuedUseCase`. */
  canInvoice: z.boolean(),
  hasAlert: z.boolean(),
  /** Qué alertas tiene — para el tooltip del ícono. Vacío ⇔ `hasAlert` falso. */
  alerts: z.array(supportPlanAlertTypeSchema),
  isOverdue: z.boolean(),
});
export type SupportBillingRow = z.infer<typeof supportBillingRowSchema>;

/**
 * KPIs de un grupo de contratos que comparten moneda — sumar montos de
 * monedas distintas sería inventar un número sin sentido, así que en vez de
 * un total único hay un grupo por cada moneda presente (en la práctica, casi
 * siempre uno solo: "UF").
 */
export const supportBillingKpiGroupSchema = z.object({
  currency: supportPlanCurrencySchema,
  toInvoiceAmount: z.number(),
  toInvoiceCount: z.number().int(),
  invoicedAmount: z.number(),
  paidAmount: z.number(),
  /** Cruza TODOS los períodos, no solo el seleccionado — igual que en el prototipo. */
  overdueAmount: z.number(),
  overdueCount: z.number().int(),
});
export type SupportBillingKpiGroup = z.infer<typeof supportBillingKpiGroupSchema>;

/**
 * KPIs del panel — cruzan TODOS los contratos activos, así que viven en su
 * propio endpoint: la tabla (`GET /support-invoices/overview/rows`) se pagina
 * en el servidor y nunca trae la lista completa.
 */
export const supportBillingOverviewSchema = z.object({
  /** 'YYYY-MM' del período consultado. */
  period: z.string(),
  perCurrency: z.array(supportBillingKpiGroupSchema),
  /** Agnóstico a moneda — cuenta contratos, no montos. */
  alertsCount: z.number().int(),
  /** Clientes con algún contrato activo, ordenados por nombre — opciones del filtro "Cliente" de la tabla. */
  clients: z.array(z.object({ id: uuidSchema, name: z.string() })),
});
export type SupportBillingOverview = z.infer<typeof supportBillingOverviewSchema>;

const periodKeySchema = z.string().regex(/^\d{4}-\d{2}$/, 'Formato de período inválido.');

export const supportBillingOverviewQuerySchema = z.object({
  /** 'YYYY-MM'. */
  period: periodKeySchema,
});
export type SupportBillingOverviewQuery = z.infer<typeof supportBillingOverviewQuerySchema>;

export const supportBillingRowSortFieldSchema = z.enum(['client', 'plan', 'type', 'amount', 'billing', 'payment']);
export type SupportBillingRowSortField = z.infer<typeof supportBillingRowSortFieldSchema>;

export const listSupportBillingRowsQuerySchema = paginationQuerySchema.extend({
  /** 'YYYY-MM'. */
  period: periodKeySchema,
  planType: supportPlanTypeSchema.optional(),
  /** Tab "Con alertas" — no es un tipo de contrato, por eso es un filtro aparte de `planType`. */
  onlyAlerts: z.stringbool().optional(),
  clientId: arrayQueryParam(uuidSchema),
  sortField: supportBillingRowSortFieldSchema.default('client'),
  sortDirection: sortDirectionSchema.default('asc'),
});
export type ListSupportBillingRowsQuery = z.infer<typeof listSupportBillingRowsQuerySchema>;

export const listSupportBillingRowsResponseSchema = pageSchema(supportBillingRowSchema);
export type ListSupportBillingRowsResponse = z.infer<typeof listSupportBillingRowsResponseSchema>;

/**
 * Libro combinado de Facturación (rol Finanzas) — une Soporte y Proyectos en
 * una sola tabla plana. Soporte: el período vigente (mismo cálculo que
 * `supportBillingOverviewSchema`) + el historial real de `support_invoices`
 * (todos los períodos). Proyectos: todos los `project_payment_milestones` de
 * todos los proyectos. Los KPIs se calculan en el cliente a partir de las
 * filas, igual que en el prototipo.
 */
export const BILLING_LEDGER_LINES = ['soporte', 'proyecto'] as const;
export const billingLedgerLineSchema = z.enum(BILLING_LEDGER_LINES);
export type BillingLedgerLine = z.infer<typeof billingLedgerLineSchema>;

export const BILLING_LEDGER_LINE_LABELS: Readonly<Record<BillingLedgerLine, string>> = {
  soporte: 'Soporte',
  proyecto: 'Proyecto',
};

/**
 * `pendiente`: Soporte — ciclo vigente sin factura; Proyecto — el hito del tramo aún no se cumple.
 * `por_facturar`: solo Proyecto — el hito se cumplió y falta emitir la factura.
 */
export const BILLING_LEDGER_STATUSES = ['pendiente', 'por_facturar', 'facturado', 'atrasado', 'pagado'] as const;
export const billingLedgerStatusSchema = z.enum(BILLING_LEDGER_STATUSES);
export type BillingLedgerStatus = z.infer<typeof billingLedgerStatusSchema>;

export const BILLING_LEDGER_STATUS_LABELS: Readonly<Record<BillingLedgerStatus, string>> = {
  pendiente: 'Pendiente',
  por_facturar: 'Por facturar',
  facturado: 'Facturado',
  atrasado: 'Atrasado',
  pagado: 'Pagado',
};

export const billingLedgerEntrySchema = z.object({
  /** Id de la fila para la lista (no siempre corresponde a una entidad — la fila "pendiente" de un contrato de soporte vigente no tiene factura todavía). */
  id: z.string(),
  line: billingLedgerLineSchema,
  clientId: uuidSchema,
  clientName: z.string(),
  /** Nombre del contrato de soporte o del tramo del proyecto. */
  detail: z.string(),
  /** Soporte: en la moneda del contrato (ver `currency`). Proyecto: el monto declarado en el tramo, tal como se guardó. */
  amount: z.number(),
  /** Soporte: la moneda real del contrato. Proyecto: la moneda del contrato del proyecto (`projects.contract_currency`). */
  currency: supportPlanCurrencySchema,
  folio: z.string().nullable(),
  issuedAt: z.iso.date().nullable(),
  status: billingLedgerStatusSchema,
  paidAt: z.iso.date().nullable(),
  /** Id de la factura de soporte o del tramo de proyecto — `null` si todavía no existe (fila "pendiente" de soporte). */
  referenceId: z.string().nullable(),
  /** Solo `line: 'proyecto'` — el proyecto dueño del tramo, para las acciones de la fila. */
  projectId: uuidSchema.nullable(),
  /** Solo `line: 'proyecto'` — nombre del proyecto, sin el del tramo (`detail` los concatena). */
  projectName: z.string().nullable(),
  /** Solo `line: 'proyecto'` — nombre del tramo (ej. "Inicio"). */
  milestoneName: z.string().nullable(),
  /** Solo `line: 'proyecto'` — porcentaje del tramo sobre el total, si se configuró. */
  milestonePercent: z.number().nullable(),
  /** Vencimiento del pago de la factura — `null` si no hay factura o no se informó. */
  dueAt: z.iso.date().nullable(),
  /** Solo `line: 'proyecto'` — qué hito deja el tramo por facturar (ver `PROJECT_PAYMENT_TRIGGERS`). */
  milestoneTriggerType: z.enum(['approval', 'phase', 'date', 'manual']).nullable(),
  milestoneTriggerPhase: z.enum(['discovery', 'development', 'testing', 'pilot', 'closed']).nullable(),
  milestoneTriggerDate: z.iso.date().nullable(),
});
export type BillingLedgerEntry = z.infer<typeof billingLedgerEntrySchema>;

export const billingLedgerResponseSchema = z.object({ entries: z.array(billingLedgerEntrySchema) });
export type BillingLedgerResponse = z.infer<typeof billingLedgerResponseSchema>;
